/**
 * The response chart's hand gestures: pinch to zoom (trackpad, Safari,
 * touch) and drag with the mouse to pan. The keyboard and the buttons are
 * `useChartViewport`'s; this file only turns raw events into its calls.
 *
 * Every browser reports a pinch differently, and each path has its trap:
 *
 *  - Chrome, Edge and Firefox send a trackpad pinch as `wheel` with
 *    `ctrlKey` set (and so does a real Ctrl + mouse wheel). Unless the
 *    listener is non-passive and cancels it, the browser zooms the whole
 *    page. Wheels are summed per animation frame: a trackpad fires several
 *    per frame, and one zoom per event would re-render the chart for each.
 *  - Safari on a Mac sends `gesturestart/gesturechange/gestureend` with a
 *    scale relative to the gesture's start, and no ctrl-wheel. Zooming from
 *    the span at the start (not the last event's) keeps it from compounding.
 *    Safari on iOS sends the same events ALONGSIDE the touches, so once a
 *    touch has been seen the gesture events are only cancelled, never used,
 *    or a pinch would zoom twice.
 *  - Two fingers on a phone: `touch-action: pan-x pan-y` on the frame keeps
 *    the browser from page-zooming, and the non-passive touchmove cancels the
 *    native scroll while both fingers are down. The time first under the
 *    fingers stays under them as they move, so a pinch can also pan. A pinch
 *    whose touchmove cannot be cancelled (one finger had already started a
 *    native pan before the second landed) is dropped for the rest of that
 *    touch rather than fought: zooming under a running pan rewrote scrollLeft
 *    every frame against it, and the view jittered off the fingers.
 *  - A plain vertical wheel is not touched: the page must still scroll when
 *    the pointer happens to cross the chart. A horizontal wheel and shift +
 *    wheel scroll the frame natively.
 *
 * Mouse drag pans by moving scrollLeft, so it goes through the same scroll
 * path as a trackpad swipe. It starts only after DRAG_PX of travel and only
 * then captures the pointer: a click or a hover never loses the tooltip.
 */
import { useEffect } from "react";
import type { ChartViewport } from "./useChartViewport";

/** Movement before a mouse press becomes a drag. */
const DRAG_PX = 4;
/** Wheel pixels to zoom: exp(deltaY * WHEEL_RATE) per event. */
const WHEEL_RATE = 0.01;
/** One wheel event zooms at most exp(30 * 0.01), about 1.35x, however far a mouse notch jumps. */
const WHEEL_MAX = 30;

/** Safari's GestureEvent, which the DOM typings do not carry. */
type GestureLike = Event & { scale?: number; clientX?: number };

type Gestures = Pick<ChartViewport, "scrollerRef" | "getView" | "anchorOf" | "zoomFrom">;

export function useChartGestures({ scrollerRef, getView, anchorOf, zoomFrom }: Gestures) {
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // ── Trackpad pinch and Ctrl + wheel ──
    let wheelFactor = 1;
    let wheelAnchor = 0.5;
    let wheelFrame = 0;
    // Safari's own pinch, while one is going on.
    let gesture: { base: ReturnType<typeof getView>; anchor: number } | null = null;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (gesture) return;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      const dy = Math.max(-WHEEL_MAX, Math.min(WHEEL_MAX, e.deltaY * unit));
      wheelFactor *= Math.exp(dy * WHEEL_RATE);
      wheelAnchor = anchorOf(e.clientX);
      if (wheelFrame) return;
      wheelFrame = requestAnimationFrame(() => {
        wheelFrame = 0;
        const base = getView();
        zoomFrom(base, base.span * wheelFactor, wheelAnchor);
        wheelFactor = 1;
      });
    };

    // ── Safari (macOS) pinch ──
    let sawTouch = false;
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      if (sawTouch) return;
      gesture = { base: getView(), anchor: anchorOf((e as GestureLike).clientX ?? 0) };
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const scale = (e as GestureLike).scale ?? 1;
      if (!gesture || !(scale > 0)) return;
      zoomFrom(gesture.base, gesture.base.span / scale, gesture.anchor);
    };
    const onGestureEnd = (e: Event) => {
      e.preventDefault();
      gesture = null;
    };

    // ── Two-finger touch pinch ──
    let pinch: { base: ReturnType<typeof getView>; d0: number; a0: number } | null = null;
    const fingers = (list: TouchList) => {
      const a = list[0]!;
      const b = list[1]!;
      return { d: Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)), x: (a.clientX + b.clientX) / 2 };
    };
    const onTouchStart = (e: TouchEvent) => {
      sawTouch = true;
      if (e.touches.length !== 2) return;
      const { d, x } = fingers(e.touches);
      pinch = { base: getView(), d0: d, a0: anchorOf(x) };
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return;
      // The browser already owns this touch as a pan: let it run alone until the fingers lift.
      if (!e.cancelable) {
        pinch = null;
        return;
      }
      e.preventDefault();
      const { d, x } = fingers(e.touches);
      const { base, d0, a0 } = pinch;
      const a = anchorOf(x);
      // The time first under the fingers, moved to where the fingers are now, then zoomed around it.
      const at = base.from + a0 * base.span;
      zoomFrom({ from: at - a * base.span, span: base.span }, (base.span * d0) / d, a);
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null;
    };

    // ── Mouse drag to pan ──
    let drag: { id: number; x0: number; s0: number; on: boolean } | null = null;
    const endDrag = () => {
      if (drag?.on) {
        delete el.dataset.dragging;
        if (el.hasPointerCapture(drag.id)) el.releasePointerCapture(drag.id);
      }
      drag = null;
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      drag = { id: e.pointerId, x0: e.clientX, s0: el.scrollLeft, on: false };
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x0;
      if (!drag.on) {
        if (Math.abs(dx) < DRAG_PX || el.scrollWidth <= el.clientWidth) return;
        drag.on = true;
        el.setPointerCapture(e.pointerId);
        el.dataset.dragging = "";
      }
      el.scrollLeft = drag.s0 - dx;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (drag && e.pointerId === drag.id) endDrag();
    };

    const passive = { passive: true } as const;
    const active = { passive: false } as const;
    el.addEventListener("wheel", onWheel, active);
    el.addEventListener("gesturestart", onGestureStart, active);
    el.addEventListener("gesturechange", onGestureChange, active);
    el.addEventListener("gestureend", onGestureEnd, active);
    el.addEventListener("touchstart", onTouchStart, passive);
    el.addEventListener("touchmove", onTouchMove, active);
    el.addEventListener("touchend", onTouchEnd, passive);
    el.addEventListener("touchcancel", onTouchEnd, passive);
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("lostpointercapture", endDrag);
    return () => {
      cancelAnimationFrame(wheelFrame);
      endDrag();
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGestureStart);
      el.removeEventListener("gesturechange", onGestureChange);
      el.removeEventListener("gestureend", onGestureEnd);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("lostpointercapture", endDrag);
    };
  }, [scrollerRef, getView, anchorOf, zoomFrom]);
}
